import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import unifiedApi, { buildApiUrl } from '../utils/unifiedApi';
import { clearAppBadge, closeChannelNotifications, syncPushSubscription } from '../utils/pushNotifications';

export const CHAT_HUB_PATH = '/hubs/chat';
const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000, 30000];
const RETRY_INTERVAL_MS = 60000;
// While the user has no channels, occasionally check whether one has been enabled for them.
const DISCOVERY_INTERVAL_MS = 5 * 60000;

const noop = () => {};
const ChatContext = createContext({
  channels: [],
  channelsLoaded: false,
  hasNewMessages: false,
  userTenantId: null,
  connectionState: 'disconnected',
  refreshChannels: noop,
  subscribe: () => noop,
  setActiveChannelId: noop,
  markChannelSeen: noop,
  setChannelMuted: noop,
  openDirectChat: async () => null,
  hideChannel: async () => {},
});

/**
 * Loads the signed-in user's chat channels and, once they're known to belong to the organization (or
 * have a channel), keeps a SignalR connection open for real-time messages, so a first direct message
 * arrives live. The server addresses each event
 * to the channel's current members, so new channels need no client-side join.
 *
 * Installed PWAs drop the socket when backgrounded, so the connection is restarted and
 * subscribers are told to resync whenever the app becomes visible or comes back online.
 */
export const ChatProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [channels, setChannels] = useState([]);
  const [channelsLoaded, setChannelsLoaded] = useState(false);
  const [userTenantId, setUserTenantId] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  // True only after a successful channel load, so the app badge isn't cleared on cold start or failure.
  const [channelsConfirmed, setChannelsConfirmed] = useState(false);

  const connectionRef = useRef(null);
  const listenersRef = useRef(new Set());
  const activeChannelIdRef = useRef(null);
  const userTenantIdRef = useRef(null);
  const refreshFailedRef = useRef(false);
  const hasChannelsRef = useRef(false);
  const knownChannelIdsRef = useRef(new Set());

  const emit = useCallback((event) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        console.error('Chat listener failed', err);
      }
    });
  }, []);

  // Tell the server which chat is open and visible, so it doesn't push notifications for it.
  const reportPresence = useCallback(() => {
    const connection = connectionRef.current;
    if (!connection || connection.state !== HubConnectionState.Connected) return;
    const visibleChannelId = document.visibilityState === 'visible' ? activeChannelIdRef.current : null;
    connection.invoke('SetActiveChannel', visibleChannelId ?? null).catch(() => {});
  }, []);

  const refreshChannels = useCallback(async () => {
    if (!isAuthenticated) {
      setChannels([]);
      setChannelsLoaded(false);
      return;
    }
    try {
      unifiedApi.clearCacheFor('/api/chat/channels');
      const data = await unifiedApi.getChatChannels();
      refreshFailedRef.current = false;
      setChannelsConfirmed(true);
      setChannels((Array.isArray(data) ? data : []).map((channel) => (
        channel.id === activeChannelIdRef.current ? { ...channel, hasNewMessages: false } : channel
      )));
    } catch {
      // Keep the last known channel list; the discovery effect retries.
      refreshFailedRef.current = true;
      setChannelsConfirmed(false);
    } finally {
      setChannelsLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    refreshChannels();
  }, [authLoading, refreshChannels]);

  // Channel discovery runs independently of the socket so a failed first load, or a first channel
  // enabled later, is picked up when the installed app returns to the foreground or comes back online.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return undefined;

    const handleResume = () => {
      if (document.visibilityState === 'visible') refreshChannels();
    };
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (refreshFailedRef.current) {
        refreshChannels();
      }
    }, RETRY_INTERVAL_MS);
    const discoveryTimer = setInterval(() => {
      if (document.visibilityState === 'visible' && !hasChannelsRef.current) refreshChannels();
    }, DISCOVERY_INTERVAL_MS);

    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('online', handleResume);
    return () => {
      clearInterval(timer);
      clearInterval(discoveryTimer);
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('online', handleResume);
    };
  }, [authLoading, isAuthenticated, refreshChannels]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUserTenantId(null);
      userTenantIdRef.current = null;
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await unifiedApi.getMe();
        if (!cancelled) {
          setUserTenantId(me?.userTenantId || null);
          userTenantIdRef.current = me?.userTenantId || null;
        }
      } catch {
        // Own messages just won't be highlighted.
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const hasChannels = channels.length > 0;
  hasChannelsRef.current = hasChannels;
  // Any member of the organization can receive a direct message, so connect even before they have a chat.
  const canConnect = isAuthenticated && (hasChannels || Boolean(userTenantId));
  knownChannelIdsRef.current = new Set(channels.map((c) => c.id));
  const anyNewMessages = channels.some((c) => c.hasNewMessages);

  // Re-save this device's push subscription for the signed-in member (also covers a different
  // member signing in on a shared browser).
  useEffect(() => {
    if (!isAuthenticated || !hasChannels) return;
    syncPushSubscription().catch(() => {});
  }, [isAuthenticated, hasChannels]);

  useEffect(() => {
    if (channelsConfirmed && !anyNewMessages) clearAppBadge();
  }, [channelsConfirmed, anyNewMessages]);

  useEffect(() => {
    const handleVisibility = () => {
      reportPresence();
      // Returning to an open conversation (even while the socket is down): its pushes are now read.
      if (document.visibilityState === 'visible' && activeChannelIdRef.current) {
        closeChannelNotifications(activeChannelIdRef.current);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [reportPresence]);

  useEffect(() => {
    if (!canConnect) return undefined;

    const connection = new HubConnectionBuilder()
      .withUrl(buildApiUrl(CHAT_HUB_PATH), {
        accessTokenFactory: async () => (await unifiedApi.getToken()) || '',
      })
      .withAutomaticReconnect(RECONNECT_DELAYS_MS)
      .configureLogging(LogLevel.Warning)
      .build();
    connectionRef.current = connection;
    let disposed = false;

    connection.on('MessageReceived', (message) => {
      emit({ type: 'message', message });
      const isActive = message.channelId === activeChannelIdRef.current && document.visibilityState === 'visible';
      const isOwn = message.senderUserTenantId === userTenantIdRef.current;
      // First message of a direct conversation someone else started: it isn't in the list yet.
      if (!knownChannelIdsRef.current.has(message.channelId)) refreshChannels();
      setChannels((prev) => prev.map((channel) => (channel.id === message.channelId
        ? {
          ...channel,
          lastMessageAt: message.createdAt,
          hasNewMessages: channel.hasNewMessages || (!isActive && !isOwn),
        }
        : channel)));
    });
    connection.on('MessageDeleted', (deleted) => emit({ type: 'deleted', ...deleted }));
    // Edits update the message in place only; they never mark a channel as having new messages.
    connection.on('MessageEdited', (message) => emit({ type: 'edited', message }));
    connection.on('ReactionsUpdated', (updated) => emit({ type: 'reactions', ...updated }));

    connection.onreconnecting(() => setConnectionState('reconnecting'));
    connection.onreconnected(() => {
      setConnectionState('connected');
      reportPresence();
      emit({ type: 'resync' });
      refreshChannels();
    });
    connection.onclose(() => {
      if (!disposed) setConnectionState('disconnected');
    });

    const start = async () => {
      if (disposed || connection.state !== HubConnectionState.Disconnected) return;
      setConnectionState('connecting');
      try {
        await connection.start();
        if (disposed) return;
        setConnectionState('connected');
        reportPresence();
        // History and unread flags were loaded over HTTP before the socket was live; catch up on
        // anything sent in between (every successful start, including the first).
        emit({ type: 'resync' });
        refreshChannels();
      } catch {
        if (!disposed) setConnectionState('disconnected');
      }
    };

    const handleResume = () => {
      if (document.visibilityState !== 'visible') return;
      if (connection.state === HubConnectionState.Disconnected) {
        start();
      } else {
        // The socket may have died silently while backgrounded; refetch to be safe.
        emit({ type: 'resync' });
      }
    };

    const retryTimer = setInterval(() => {
      if (document.visibilityState === 'visible' && connection.state === HubConnectionState.Disconnected) start();
    }, RETRY_INTERVAL_MS);

    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('online', handleResume);
    start();

    return () => {
      disposed = true;
      clearInterval(retryTimer);
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('online', handleResume);
      connectionRef.current = null;
      setConnectionState('disconnected');
      connection.stop().catch(() => {});
    };
  }, [canConnect, emit, refreshChannels, reportPresence]);

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const setActiveChannelId = useCallback((channelId) => {
    activeChannelIdRef.current = channelId;
    if (channelId) {
      setChannels((prev) => prev.map((c) => (c.id === channelId ? { ...c, hasNewMessages: false } : c)));
      closeChannelNotifications(channelId);
    }
    reportPresence();
  }, [reportPresence]);

  /** Mutes/unmutes push notifications for a chat. Throws if the request fails. */
  const setChannelMuted = useCallback(async (channelId, muted) => {
    await unifiedApi.setChatChannelMuted(channelId, muted);
    setChannels((prev) => prev.map((c) => (c.id === channelId ? { ...c, isMuted: muted } : c)));
  }, []);

  // `lastMessageId` is the newest message actually displayed, so anything newer stays unread.
  const markChannelSeen = useCallback(async (channelId, lastMessageId) => {
    setChannels((prev) => prev.map((c) => (c.id === channelId ? { ...c, hasNewMessages: false } : c)));
    try {
      await unifiedApi.markChatChannelSeen(channelId, lastMessageId);
    } catch {
      // Non-critical; the dot may reappear on the next refresh.
    }
  }, []);

  /**
   * Opens (creating on first use) the direct conversation with another member and adds it to the list.
   * Returns the channel, or null when the member can't be messaged. Throws if the request fails.
   */
  const openDirectChat = useCallback(async (otherUserTenantId) => {
    const channel = await unifiedApi.openDirectChat(otherUserTenantId);
    if (!channel) return null;
    setChannels((prev) => (prev.some((c) => c.id === channel.id)
      ? prev.map((c) => (c.id === channel.id ? { ...c, ...channel } : c))
      : [channel, ...prev]));
    return channel;
  }, []);

  /**
   * Deletes a direct conversation from the list (the messages are kept). It comes back when a new message
   * arrives, since a message for an unknown channel refreshes the list. Throws if the request fails.
   */
  const hideChannel = useCallback(async (channelId) => {
    await unifiedApi.hideChatChannel(channelId);
    unifiedApi.clearCacheFor('/api/chat/channels');
    setChannels((prev) => prev.filter((c) => c.id !== channelId));
    await refreshChannels();
  }, [refreshChannels]);

  const value = useMemo(() => ({
    channels,
    channelsLoaded,
    hasNewMessages: channels.some((c) => c.hasNewMessages),
    userTenantId,
    connectionState,
    refreshChannels,
    subscribe,
    setActiveChannelId,
    markChannelSeen,
    setChannelMuted,
    openDirectChat,
    hideChannel,
  }), [channels, channelsLoaded, userTenantId, connectionState, refreshChannels, subscribe, setActiveChannelId, markChannelSeen, setChannelMuted, openDirectChat, hideChannel]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => useContext(ChatContext);
